import React, { useEffect, useState } from "react";
import { Flame, Video, Pencil, Pause, Play, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { useAuth } from "../context/AuthContext";
import { AddToCalendar } from "./AddToCalendar";
import { useNetText } from "./HearthEvents";
import { RecordingNotice, RecordToggle } from "./RecordingPlaybook";

const FIRE = "#E8A33D";
const FREQS = [["weekly", "Every week"], ["biweekly", "Every 2 weeks"], ["monthly", "Every month"]];
const LENGTHS = [30, 45, 60, 90, 120];
const ORD = ["first", "second", "third", "fourth", "last"];
const DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

const nthOfMonth = (d) => Math.min(Math.ceil(d.getDate() / 7), 5);
const monthlyDate = (y, mo, wd, n, h, m) => {
  if (n >= 5) { const last = new Date(y, mo + 1, 0, h, m); last.setDate(last.getDate() - ((last.getDay() - wd + 7) % 7)); return last; }
  const first = new Date(y, mo, 1, h, m);
  return new Date(y, mo, 1 + ((wd - first.getDay() + 7) % 7) + (n - 1) * 7, h, m);
};

// The current or next gathering, in the viewer's own time zone.
export const nextFireside = (fs, now = Date.now()) => {
  const start = new Date(fs.starts_at);
  const dur = (fs.duration_min || 60) * 60e3;
  if (start.getTime() + dur > now) return start;
  if (fs.freq !== "monthly") {
    const days = fs.freq === "biweekly" ? 14 : 7;
    const d = new Date(start);
    d.setDate(d.getDate() + Math.floor((now - start.getTime()) / (days * 864e5)) * days);
    while (d.getTime() + dur <= now) d.setDate(d.getDate() + days);
    return d;
  }
  const n = nthOfMonth(start), nd = new Date(now);
  for (let i = -1; i < 3; i++) {
    const d = monthlyDate(nd.getFullYear(), nd.getMonth() + i, start.getDay(), n, start.getHours(), start.getMinutes());
    if (d >= start && d.getTime() + dur > now) return d;
  }
  return start;
};

const patternLabel = (fs) => {
  const s = new Date(fs.starts_at);
  const day = s.toLocaleDateString([], { weekday: "long" });
  const time = s.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (fs.freq === "monthly") return `Every ${ORD[nthOfMonth(s) - 1]} ${day} of the month, ${time}`;
  if (fs.freq === "biweekly") return `Every other ${day}, ${time}`;
  return `Every ${day}, ${time}`;
};

const rruleFor = (fs) => {
  const s = new Date(fs.starts_at);
  if (fs.freq === "monthly") { const n = nthOfMonth(s); return `FREQ=MONTHLY;BYDAY=${n >= 5 ? -1 : n}${DAYS[s.getDay()]}`; }
  return fs.freq === "biweekly" ? "FREQ=WEEKLY;INTERVAL=2" : "FREQ=WEEKLY";
};

const toLocalInput = (iso) => {
  const d = new Date(iso), p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const FiresideForm = ({ hearthId, fs, initial, onDone }) => {
  const { hasNetwork, encryptNetText } = useCrypto();
  const [name, setName] = useState(initial?.title || "");
  const [link, setLink] = useState(initial?.link || "");
  const [when, setWhen] = useState(fs ? toLocalInput(nextFireside(fs).toISOString()) : "");
  const [freq, setFreq] = useState(fs?.freq || "weekly");
  const [len, setLen] = useState(fs?.duration_min || 60);
  const [mayRecord, setMayRecord] = useState(!!fs?.may_record);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!when) return toast.error("Pick the day and time of the first gathering");
    if (!/^https:\/\/\S+$/i.test(link.trim())) return toast.error("Add a meeting link starting with https://");
    if (!hasNetwork()) return toast.error("Unlock your vault first");
    setBusy(true);
    try {
      const [t, l] = await Promise.all([encryptNetText(name.trim() || "Fireside"), encryptNetText(link.trim())]);
      const { data } = await api.put(`/hearths/${hearthId}/fireside`, { title_ct: t.ct, title_iv: t.iv, link_ct: l.ct, link_iv: l.iv,
        starts_at: new Date(when).toISOString(), freq, duration_min: Number(len), paused: !!fs?.paused, may_record: mayRecord, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      toast.success(fs ? "Fireside updated" : "Fireside is set, members were notified");
      onDone(data.fireside);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the Fireside"); } finally { setBusy(false); }
  };
  const inp = "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#E8A33D] px-3 py-2 text-white font-body text-sm outline-none";
  return (
    <div className="border border-[#E8A33D]/40 bg-[#121212] p-4 my-4 space-y-2.5" data-testid="fireside-form">
      <div className="font-body text-xs uppercase tracking-wider flex items-center gap-1.5" style={{ color: FIRE }}><Flame size={13} /> {fs ? "Edit your Fireside" : "Set up a Fireside"}</div>
      <p className="font-body text-xs text-zinc-400">One recurring gathering with one lasting Proton Meet link, pinned at the top of your Hearth.</p>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Name (optional), e.g. Tuesday Circle" data-testid="fireside-name" className={inp} />
      <label className="block font-body text-[11px] uppercase tracking-wider text-zinc-500">{fs ? "Next gathering" : "First gathering"}
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="fireside-when"
          className={`${inp} mt-1 block min-w-0 max-w-full appearance-none [color-scheme:dark] min-h-[42px] normal-case tracking-normal`} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block font-body text-[11px] uppercase tracking-wider text-zinc-500">Repeats
          <select value={freq} onChange={(e) => setFreq(e.target.value)} data-testid="fireside-freq" className={`${inp} mt-1 normal-case tracking-normal [color-scheme:dark]`}>
            {FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block font-body text-[11px] uppercase tracking-wider text-zinc-500">Length
          <select value={len} onChange={(e) => setLen(e.target.value)} data-testid="fireside-length" className={`${inp} mt-1 normal-case tracking-normal [color-scheme:dark]`}>
            {LENGTHS.map((m) => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? "s" : ""}`}</option>)}
          </select>
        </label>
      </div>
      <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Lasting Proton Meet link: https://meet.proton.me/…" data-testid="fireside-link" className={inp} />
      <p className="font-body text-[11px] text-zinc-500">Tip: in Proton Calendar, create a repeating event with a Meet link and paste that link here. The same link works every time.</p>
      <RecordToggle on={mayRecord} setOn={setMayRecord} testid="fireside-may-record" />
      <p className="font-body text-[11px] text-zinc-500">Members get a gentle reminder about 15 minutes before each gathering.</p>
      <div className="flex justify-end gap-2">
        <button onClick={() => onDone(undefined)} data-testid="fireside-cancel" className="text-zinc-400 font-body text-xs px-3 py-1.5">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="fireside-save" className="text-[#0A0A0A] font-semibold px-4 py-1.5 font-body text-xs disabled:opacity-40 flex items-center gap-1.5" style={{ background: FIRE }}>
          {busy && <Loader2 size={12} className="animate-spin" />}{fs ? "Save" : "Light the Fireside"}
        </button>
      </div>
    </div>
  );
};

export const FiresideCard = ({ hearthId, fs, isOwner, onChange }) => {
  const title = useNetText(fs.title_ct, fs.title_iv);
  const link = useNetText(fs.link_ct, fs.link_iv);
  const [editing, setEditing] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  if (fs.paused && !isOwner) return null;
  if (editing && title !== null && link !== null) {
    return <FiresideForm hearthId={hearthId} fs={fs} initial={{ title: title === "Fireside" ? "" : title, link }} onDone={(next) => { setEditing(false); if (next) onChange(next); }} />;
  }
  const next = nextFireside(fs);
  const now = Date.now();
  const live = !fs.paused && now >= next.getTime() - 10 * 60e3 && now < next.getTime() + (fs.duration_min || 60) * 60e3;
  const setPaused = async (paused) => {
    try { const { data } = await api.patch(`/hearths/${hearthId}/fireside`, { paused }); onChange(data.fireside); toast.success(paused ? "Fireside paused, members no longer see it" : "Fireside is back"); }
    catch { toast.error("Couldn't update the Fireside"); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this Fireside? Members will no longer see it.")) return;
    try { await api.delete(`/hearths/${hearthId}/fireside`); onChange(null); toast("Fireside removed"); }
    catch { toast.error("Couldn't remove the Fireside"); }
  };
  const named = title && title !== "Fireside";
  return (
    <div className={`relative overflow-hidden border p-4 my-4 ${live ? "border-[#E8A33D] bg-[#E8A33D]/10" : "border-[#E8A33D]/35 bg-[#E8A33D]/[0.04]"}`} data-testid="fireside-card">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: FIRE }} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-body text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5 mb-1" style={{ color: FIRE }}>
            <Flame size={12} /> Fireside{live && <span className="tracking-wider"> · Happening now</span>}{fs.paused && <span className="text-zinc-500 tracking-wider"> · Paused</span>}
          </div>
          {named && <div className="font-body text-white font-medium break-words" data-testid="fireside-title">{title}</div>}
          <div className="font-body text-sm text-zinc-200" data-testid="fireside-pattern">{patternLabel(fs)}</div>
          {!fs.paused && !live && <div className="font-body text-xs text-zinc-400 mt-0.5" data-testid="fireside-next">Next: {next.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>}
        </div>
        {isOwner && (
          <div className="flex items-center gap-2.5 shrink-0 text-zinc-500">
            <button onClick={() => setEditing(true)} title="Edit" data-testid="fireside-edit" className="hover:text-[#E8A33D]"><Pencil size={13} /></button>
            <button onClick={() => setPaused(!fs.paused)} title={fs.paused ? "Resume" : "Pause"} data-testid="fireside-pause" className="hover:text-[#E8A33D]">{fs.paused ? <Play size={13} /> : <Pause size={13} />}</button>
            <button onClick={remove} title="Remove" data-testid="fireside-remove" className="hover:text-red-400"><Trash2 size={13} /></button>
          </div>
        )}
      </div>
      {!fs.paused && (
        <div className="relative">
          {fs.may_record && <RecordingNotice testid="fireside-recording" />}
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" data-testid="fireside-join"
              className={`mt-3 inline-flex items-center gap-1.5 font-body text-xs px-3 py-1.5 transition-colors ${live ? "text-[#0A0A0A] font-semibold" : "border border-[#E8A33D]/50 text-[#E8A33D] hover:bg-[#E8A33D]/10"}`}
              style={live ? { background: FIRE } : undefined}>
              <Video size={13} /> {live ? "Join now" : "Meeting link"}
            </a>
          )}
          {!link && <p className="mt-3 font-body text-xs text-zinc-500" data-testid="fireside-locked">The Meet link appears here once your vault is unlocked.</p>}
          {title !== null && <AddToCalendar label="Add the series to my calendar" ev={{ id: `fireside-${hearthId}`, uid: `fireside-${hearthId}`, title, startsAt: fs.starts_at,
            durationMin: fs.duration_min, rrule: rruleFor(fs), link, hearthUrl: `${window.location.origin}/hearth/${hearthId}` }} />}
        </div>
      )}
    </div>
  );
};

const SUGGEST_ID = "fireside-suggest";

export const FiresideSuggest = ({ onStart }) => {
  const { user, dismissTip } = useAuth();
  if ((user?.dismissed_tips || []).includes(`${SUGGEST_ID}@1`)) return null;
  return (
    <div className="relative border border-[#E8A33D]/30 bg-[#E8A33D]/[0.04] p-4 my-4" data-testid="fireside-suggest">
      <button onClick={() => dismissTip(SUGGEST_ID, 1)} title="Not now" data-testid="fireside-suggest-dismiss" className="absolute top-3 right-3 text-zinc-600 hover:text-white"><X size={14} /></button>
      <div className="font-body text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5 mb-1.5" style={{ color: FIRE }}><Flame size={12} /> Only you see this</div>
      <p className="font-body text-sm text-white pr-6">Gather face to face with a Fireside.</p>
      <p className="font-body text-xs text-zinc-400 mt-1 leading-relaxed">Set one recurring Proton Meet gathering, weekly, every 2 weeks or monthly, and it stays pinned at the top of this Hearth. Members always know when and where to find you, so stepping into the call feels like walking through a door they already know.</p>
      <button onClick={onStart} data-testid="fireside-suggest-start" className="mt-3 inline-flex items-center gap-1.5 font-body text-xs font-semibold text-[#0A0A0A] px-3.5 py-1.5" style={{ background: FIRE }}><Flame size={13} /> Set up a Fireside</button>
    </div>
  );
};
